import { Editor, Element as SlateElement, Range, Transforms } from 'slate';

import type { BlockFormat, CustomElement, MarkFormat } from '../../types/RichTextEditor';

export const isMarkActive = (editor: Editor, format: MarkFormat): boolean => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

export const isBlockActive = (editor: Editor, format: BlockFormat): boolean => {
  const { selection } = editor;
  if (!selection) return false;
  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) => SlateElement.isElement(n) && n.type === format,
    }),
  );
  return !!match;
};

export const toggleMark = (editor: Editor, format: MarkFormat): void => {
  if (isMarkActive(editor, format)) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

export const toggleList = (editor: Editor, format: 'bulleted-list' | 'numbered-list'): void => {
  const isActive = isBlockActive(editor, format);
  Transforms.unwrapNodes(editor, {
    match: (n) => SlateElement.isElement(n) && (n.type === 'bulleted-list' || n.type === 'numbered-list'),
    split: true,
  });
  Transforms.setNodes<CustomElement>(editor, { type: isActive ? 'paragraph' : 'list-item' });
  if (!isActive) {
    Transforms.wrapNodes(editor, { type: format, children: [] });
  }
};

export const withLinks = <T extends Editor>(editor: T): T => {
  const { isInline } = editor;
  editor.isInline = (element) => element.type === 'link' || isInline(element);
  return editor;
};

export const unwrapLink = (editor: Editor): void => {
  Transforms.unwrapNodes(editor, { match: (n) => SlateElement.isElement(n) && n.type === 'link' });
};

export const wrapLink = (editor: Editor, url: string): void => {
  if (isBlockActive(editor, 'link')) unwrapLink(editor);
  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const link: CustomElement = { type: 'link', url, children: isCollapsed ? [{ text: url }] : [] };
  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
  }
};

export const insertLink = (editor: Editor, url: string): void => {
  if (editor.selection) wrapLink(editor, url);
};
